import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const TOP_BAR_BTN =
  "w-9 h-9 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.10] text-neutral-200 hover:text-white flex items-center justify-center transition-colors duration-200";
const NAV_BTN =
  "absolute top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.10] text-neutral-200 hover:text-white flex items-center justify-center transition-colors duration-200 z-10";

const LightboxTopBar = ({ index, total, label, onClose }) => (
  <div className="absolute top-4 left-0 right-0 flex items-center justify-between px-5 sm:px-8 z-10">
    <div className="text-[12.5px] text-neutral-300 font-mono tabular-nums">
      {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      {label && <span className="ml-3 font-sans text-neutral-400">{label}</span>}
    </div>
    <button type="button" onClick={onClose} aria-label="Close" className={TOP_BAR_BTN}>
      <X className="w-4 h-4" strokeWidth={2.25} />
    </button>
  </div>
);

const LightboxImage = ({ src, alt, caption, fit }) => {
  const fitClass = fit === "cover" ? "object-cover" : "object-contain";
  return (
    <figure
      className="relative z-10 max-w-[1200px] w-full max-h-[80vh] flex flex-col items-center"
      onClick={(e) => e.stopPropagation()}
    >
      <img
        src={src}
        alt={alt}
        className={`max-w-full max-h-[80vh] w-auto h-auto rounded-xl shadow-[0_30px_120px_-20px_rgba(0,0,0,0.6)] ${fitClass}`}
      />
      {caption && (
        <figcaption className="mt-5 text-center text-[13px] text-neutral-300 max-w-[640px]">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

const LightboxNav = ({ onPrev, onNext }) => (
  <>
    <button
      type="button"
      onClick={onPrev}
      aria-label="Previous image"
      className={`${NAV_BTN} left-2 sm:left-6`}
    >
      <ChevronLeft className="w-5 h-5" strokeWidth={2.25} />
    </button>
    <button
      type="button"
      onClick={onNext}
      aria-label="Next image"
      className={`${NAV_BTN} right-2 sm:right-6`}
    >
      <ChevronRight className="w-5 h-5" strokeWidth={2.25} />
    </button>
  </>
);

const ImageLightbox = ({ open, screens, index, onClose, onIndex, fit = "contain" }) => {
  const total = screens?.length || 0;

  const next = useCallback(() => {
    if (!total) return;
    onIndex((index + 1) % total);
  }, [index, total, onIndex]);

  const prev = useCallback(() => {
    if (!total) return;
    onIndex((index - 1 + total) % total);
  }, [index, total, onIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, next, prev]);

  if (!open || !screens || index == null) return null;
  const current = screens[index];
  if (!current) return null;

  const node = (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4 sm:px-10 py-10"
      role="dialog"
      aria-modal="true"
      aria-label={current?.label || "Image preview"}
      style={{ animation: "lightboxFadeIn 200ms ease-out both" }}
    >
      {/* Backdrop */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close image preview"
        className="absolute inset-0 bg-neutral-950/85 backdrop-blur-md cursor-zoom-out"
      />

      <LightboxTopBar
        index={index}
        total={total}
        label={current?.label}
        onClose={onClose}
      />

      <LightboxImage
        src={current.url}
        alt={current.label}
        caption={current.caption}
        fit={fit}
      />

      {total > 1 && <LightboxNav onPrev={prev} onNext={next} />}

      <style>{`
        @keyframes lightboxFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );

  // Portal to document.body so we escape any parent overflow:hidden / transform
  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
};

export default ImageLightbox;
